import { useState, useRef, useEffect, type FormEvent, type KeyboardEvent, type ClipboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useDispatch } from 'react-redux';
import { apiPost } from '@/api/client';
import { tokenReceived } from '@/store/authSlice';
import type { AppDispatch } from '@/store/store';
import { fetchAuthConfig } from '@/env';
import {
  Page,
  Card,
  Title,
  Form,
  SubmitButton,
  ErrorMessage,
  SuccessMessage,
  CodeInputRow,
  CodeCharInput,
  StatusText,
} from './AcceptInvitationPage.styled';

const CODE_LENGTH = 6;

interface VerifyResponse {
  accessToken: string;
}

export default function AcceptInvitationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') ?? '';
  const emailFromUrl = params.get('email') ?? '';

  const [phase, setPhase] = useState<'verifying' | 'success' | 'code' | 'error'>(token ? 'verifying' : 'code');
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState('');

  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Load turnstile site key
  useEffect(() => {
    fetchAuthConfig().then((config) => setTurnstileSiteKey(config.turnstileSiteKey));
  }, []);

  // Auto-verify magic link token on mount
  useEffect(() => {
    if (!token) return;

    const verifyMagicLink = async () => {
      try {
        const result = await apiPost<VerifyResponse>('/auth/verify-magic-link', { token });
        dispatch(tokenReceived({ accessToken: result.accessToken }));
        setPhase('success');
        setTimeout(() => navigate('/sprawy', { replace: true }), 1000);
      } catch {
        setPhase('code');
        setError(t('auth.invite.error'));
      }
    };

    verifyMagicLink();
  }, [token, dispatch, navigate, t]);

  const submitCode = async (codeValue: string[]) => {
    const fullCode = codeValue.join('').toUpperCase();
    if (fullCode.length !== CODE_LENGTH) return;

    setError(null);
    setIsSubmitting(true);

    try {
      const turnstileToken = turnstileRef.current?.getResponse() ?? '';
      const result = await apiPost<VerifyResponse>('/auth/verify-code', {
        email: emailFromUrl,
        code: fullCode,
        turnstileToken,
      });
      dispatch(tokenReceived({ accessToken: result.accessToken }));
      navigate('/sprawy', { replace: true });
    } catch (err: unknown) {
      const status = (err as { status?: number }).status;
      if (status === 410) {
        setError(t('auth.code.expired'));
      } else if (status === 401 || status === 400) {
        setError(t('auth.code.invalid'));
      } else {
        setError(t('auth.code.errorGeneric'));
      }
      setCode(Array(CODE_LENGTH).fill(''));
      codeInputRefs.current[0]?.focus();
    } finally {
      setIsSubmitting(false);
      turnstileRef.current?.reset();
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    if (value.length > 1) return;
    const next = [...code];
    next[index] = value.toUpperCase();
    setCode(next);

    if (value && index < CODE_LENGTH - 1) {
      codeInputRefs.current[index + 1]?.focus();
    }

    if (value && index === CODE_LENGTH - 1) {
      submitCode(next);
    }
  };

  const handleCodeKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\s/g, '').toUpperCase().slice(0, CODE_LENGTH);
    if (!pasted) return;

    const next = Array(CODE_LENGTH).fill('');
    for (let i = 0; i < pasted.length && i < CODE_LENGTH; i++) {
      next[i] = pasted[i];
    }
    setCode(next);

    if (pasted.length >= CODE_LENGTH) {
      submitCode(next);
    } else {
      codeInputRefs.current[pasted.length]?.focus();
    }
  };

  return (
    <Page>
      <Card>
        <Title>{t('auth.invite.title')}</Title>

        {phase === 'verifying' && <StatusText>{t('auth.invite.verifying')}</StatusText>}

        {phase === 'success' && <SuccessMessage>{t('auth.invite.success')}</SuccessMessage>}

        {phase === 'error' && <ErrorMessage>{error}</ErrorMessage>}

        {phase === 'code' && (
          <>
            <StatusText>{t('auth.invite.enterCode')}</StatusText>
            <Form
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                submitCode(code);
              }}
            >
              {error && <ErrorMessage>{error}</ErrorMessage>}
              <CodeInputRow>
                {code.map((char, i) => (
                  <CodeCharInput
                    key={i}
                    ref={(el) => {
                      codeInputRefs.current[i] = el;
                    }}
                    type="text"
                    inputMode="text"
                    maxLength={1}
                    autoComplete="one-time-code"
                    value={char}
                    onChange={(e) => handleCodeChange(i, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(i, e)}
                    onPaste={i === 0 ? handleCodePaste : undefined}
                    disabled={isSubmitting}
                    autoFocus={i === 0}
                  />
                ))}
              </CodeInputRow>

              {turnstileSiteKey && (
                <Turnstile ref={turnstileRef} siteKey={turnstileSiteKey} options={{ size: 'invisible' }} />
              )}

              <SubmitButton type="submit" disabled={isSubmitting || code.join('').length !== CODE_LENGTH}>
                {isSubmitting ? t('auth.code.verifying') : t('auth.code.verify')}
              </SubmitButton>
            </Form>
          </>
        )}
      </Card>
    </Page>
  );
}
